import { useCallback, useEffect, useState } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SOCKET_EVENTS } from '@maios/shared';
import { getSocket } from '../../lib/socket';
import { useAuthStore } from '../../store/authStore';
import { usePanelContext } from '../../contexts/PanelContext';
import { useChatUI } from '../../hooks/useChatUI';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';
import ResizeHandle from '../ui/ResizeHandle';
import Spinner from '../ui/Spinner';
import PinnedMessageBanner from './PinnedMessageBanner';
import PinnedMessageList from './PinnedMessageList';
import ThreadPanel from './ThreadPanel';
import { getChannels, getChannel, getPinnedMessages } from '../../services/chatChannelService';

const MIN_WIDTH = 280;
const MAX_WIDTH = 520;

export default function ChatSidePanel({ projectId, onVisible }: { projectId: string; onVisible?: () => void }) {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  const { chatPanelWidth, setChatPanelWidth, toggleChatPanel, openDetailPanel } = usePanelContext();

  const {
    messages,
    isLoading,
    loadingMore,
    hasMore,
    sending,
    sendMessage,
    loadMore,
    markAsRead,
    enterChat,
    leaveChat,
    showNewMsgBtn,
    handleNewMsgBtnClick,
    handleScrollChange,
    scrollContainerRef,
    messagesEndRef,
  } = useChatUI();

  // REQ-V34-012: 패널 (재)마운트 시 소켓 룸 참가 + 읽음 처리 + 뱃지 해제
  useEffect(() => {
    enterChat();
    markAsRead();
    onVisible?.();
    return () => leaveChat();
  }, [enterChat, leaveChat]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMentionClick = useCallback(
    (issueKey: string) => {
      navigate(`/projects/${key}/issues?search=${issueKey}`);
    },
    [navigate, key],
  );

  // REQ-V35-006: MentionCard 클릭 → 작업 상세 패널 열기
  const handleMentionCardClick = useCallback(
    (issueId: string) => {
      openDetailPanel('issue', issueId);
    },
    [openDetailPanel],
  );

  const handleResize = useCallback(
    (delta: number) => {
      setChatPanelWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, chatPanelWidth + delta)));
    },
    [chatPanelWidth, setChatPanelWidth],
  );

  // CHG-131: 액션 버튼(편집/삭제/리액션/핀/스레드)이 보이려면 channelId/role/onOpenThread 필요.
  // 사이드 패널은 프로젝트 메인 채널을 보여주므로 해당 채널 정보를 조회한다.
  const { data: projectChannel } = useQuery({
    queryKey: ['chatChannels', 'project', projectId],
    queryFn: () => getChannels({ type: 'PROJECT' }),
    select: (d) => d.channels.find((ch) => ch.projectId === projectId),
  });
  const channelId = projectChannel?.id;

  // CHG-131: 채널 소켓 룸 가입 — 채널 이벤트(CHANNEL_THREAD_MESSAGE / CHANNEL_MESSAGE_PINNED) 수신
  useEffect(() => {
    if (!channelId) return;
    const socket = getSocket();
    if (!socket) return;
    socket.emit(SOCKET_EVENTS.CHANNEL_JOIN, { channelId });
    return () => {
      socket.emit(SOCKET_EVENTS.CHANNEL_LEAVE, { channelId });
    };
  }, [channelId]);

  const { data: channelDetail } = useQuery({
    queryKey: ['chatChannel', channelId],
    queryFn: () => getChannel(channelId!),
    enabled: !!channelId,
  });
  const currentUserRole = channelDetail?.myRole ?? null;
  const queryClient = useQueryClient();
  const invalidatePins = useCallback(() => {
    if (channelId) queryClient.invalidateQueries({ queryKey: ['pinnedMessages', channelId] });
  }, [queryClient, channelId]);

  // CHG-131: 사이드 패널 자체를 스레드 모드로 전환 (LNB로 빠지지 않음)
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const handleOpenThread = useCallback((messageId: string) => setActiveThreadId(messageId), []);
  const handleBackFromThread = useCallback(() => setActiveThreadId(null), []);

  // CHG-131: 고정 메시지 배너/목록
  const { data: pinnedData } = useQuery({
    queryKey: ['pinnedMessages', channelId],
    queryFn: () => getPinnedMessages(channelId!, 1, 1),
    enabled: !!channelId,
  });
  const pinnedCount = pinnedData?.pagination?.total ?? 0;
  const [showPinnedList, setShowPinnedList] = useState(false);
  const handleJumpToMessage = useCallback((messageId: string) => {
    if (channelId) navigate(`/chat/${channelId}?messageId=${messageId}`);
  }, [channelId, navigate]);

  return (
    <div
      className="relative flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-[-2px_0_8px_rgba(0,0,0,0.04)] ml-3 shrink-0 overflow-hidden animate-slide-in-right"
      style={{ width: chatPanelWidth }}
    >
      <ResizeHandle onResize={handleResize} />

      {/* Header — 스레드 모드 시 좌측에 뒤로가기 + 제목 변경 */}
      <div className="flex items-center justify-between h-12 px-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-1 min-w-0">
          {activeThreadId && (
            <button
              onClick={handleBackFromThread}
              className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title="채팅으로 돌아가기"
            >
              <ArrowLeft className="size-4" />
            </button>
          )}
          <span className="text-sm font-semibold text-gray-900 truncate">
            {activeThreadId ? '스레드' : '채팅'}
          </span>
        </div>
        <button
          onClick={toggleChatPanel}
          className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="닫기"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* CHG-131: 활성 스레드 ID가 있으면 사이드 패널 자체를 스레드 모드로 (LNB 이동 없음) */}
      {activeThreadId && channelId ? (
        <ThreadPanel
          channelId={channelId}
          parentMessageId={activeThreadId}
          projectId={projectId}
          onClose={handleBackFromThread}
          channelType="PROJECT"
          embedded
        />
      ) : (
        <>
          {/* 고정 메시지 배너 + 목록 */}
          {channelId && (
            <div className="relative shrink-0">
              {pinnedCount > 0 && (
                <PinnedMessageBanner
                  channelId={channelId}
                  onToggleList={() => setShowPinnedList((v) => !v)}
                  onJumpToMessage={handleJumpToMessage}
                />
              )}
              <PinnedMessageList
                channelId={channelId}
                isOpen={showPinnedList}
                onClose={() => setShowPinnedList(false)}
                onJumpToMessage={handleJumpToMessage}
                currentUserRole={currentUserRole}
                currentUserId={userId}
              />
            </div>
          )}

          {/* Messages */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center min-h-0">
              <Spinner className="size-6" />
            </div>
          ) : (
            <ChatMessageList
              messages={messages}
              isLoading={isLoading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              onLoadMore={loadMore}
              currentUserId={userId}
              onMentionClick={handleMentionClick}
              onMentionCardClick={handleMentionCardClick}
              showNewMsgBtn={showNewMsgBtn}
              onNewMsgBtnClick={handleNewMsgBtnClick}
              scrollContainerRef={scrollContainerRef as React.RefObject<HTMLDivElement>}
              messagesEndRef={messagesEndRef as React.RefObject<HTMLDivElement>}
              onScrollChange={handleScrollChange}
              compact
              channelType="PROJECT"
              contextProjectId={projectId}
              channelId={channelId}
              currentUserRole={currentUserRole}
              onOpenThread={handleOpenThread}
              onPinChange={invalidatePins}
            />
          )}

          {/* Input */}
          <ChatInput
            projectId={projectId}
            channelId={channelId}
            onSend={(content, _attachments, mentions) => sendMessage(content, mentions)}
            sending={sending}
            compact
            channelMembers={channelDetail?.members?.map((m) => ({ userId: m.userId, name: m.name, avatarUrl: m.avatarUrl }))}
            currentUserRole={currentUserRole}
            currentUserId={userId}
          />
        </>
      )}
    </div>
  );
}
